import React from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  indeterminate?: boolean;
  className?: string;
  id?: string;
  name?: string;
  value?: string;
  size?: 'sm' | 'md' | 'lg';
  'aria-label'?: string;
  'aria-describedby'?: string;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      defaultChecked,
      onCheckedChange,
      disabled,
      indeterminate,
      className,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const [_, setInternalChecked] = React.useState(defaultChecked || false);

    const isControlled = checked !== undefined;
    const checkedValue = isControlled ? checked : internalChecked;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = event.target.checked;

      if (!isControlled) {
        setInternalChecked(newChecked);
      }

      onCheckedChange?.(newChecked);
    };

    const sizeClasses = {
      sm: 'h-3 w-3',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    };

    const iconSizeClasses = {
      sm: 'h-2 w-2',
      md: 'h-3 w-3',
      lg: 'h-4 w-4',
    };

    return (
      <div className="relative inline-flex items-center">
        <input
          ref={ref}
          type="checkbox"
          checked={checkedValue}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only"
          {...props}
        />
        <div
          role="checkbox"
          aria-checked={indeterminate ? 'mixed' : checkedValue}
          className={cn(
            'peer inline-flex items-center justify-center rounded border-2 border-gray-300',
            'bg-white text-white transition-colors duration-200 ease-in-out',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2',
            'hover:border-gray-400',
            checkedValue &&
              !indeterminate &&
              'bg-red-600 border-red-600 hover:bg-red-700 hover:border-red-700',
            indeterminate && 'bg-red-600 border-red-600 hover:bg-red-700 hover:border-red-700',
            disabled && 'opacity-50 cursor-not-allowed hover:border-gray-300',
            disabled && checkedValue && 'hover:bg-red-600 hover:border-red-600',
            sizeClasses[size],
            className
          )}
          onClick={() => {
            if (!disabled) {
              const newChecked = !checkedValue;
              if (!isControlled) {
                setInternalChecked(newChecked);
              }
              onCheckedChange?.(newChecked);
            }
          }}
        >
          {indeterminate ? (
            <Minus className={cn('text-white', iconSizeClasses[size])} />
          ) : checkedValue ? (
            <Check className={cn('text-white', iconSizeClasses[size])} />
          ) : null}
        </div>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export type { CheckboxProps };
